import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import {
  Bell, MessageSquare, Users, Video, ArrowLeft, Check, X, Clock,
  ChevronRight, Loader2
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Notifications() {
  const [unreadChats, setUnreadChats] = useState([]);
  const [unreadGroups, setUnreadGroups] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setIsLoading(true);
    const headers = getHeaders();

    try {
      // Fetch comprehensive notification summary
      const summaryRes = await axios.get(`${API}/comms/notifications/summary`, {
        headers,
        withCredentials: true
      });
      
      const summary = summaryRes.data;
      setPendingInvites(summary.pending_invites || []);
      setUnreadChats(summary.unread_chats || []);
      setUnreadGroups(summary.unread_groups || []);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      // Fallback to individual endpoints
      try {
        const invitesRes = await axios.get(`${API}/comms/meetings/invites`, {
          headers,
          withCredentials: true
        });
        setPendingInvites(invitesRes.data || []);
      } catch (e) {
        console.error("Failed to fetch invites");
      }
    }

    setIsLoading(false);
  };

  const respondToInvite = async (meetingId, response) => {
    try {
      await axios.post(`${API}/comms/meetings/${meetingId}/respond`, {
        response,
        language: "en"
      }, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success(response === "confirm" ? "Invitation accepted" : "Invitation declined");
      setPendingInvites(prev => prev.filter(i => i.id !== meetingId));
    } catch (err) {
      toast.error("Failed to respond to invitation");
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const totalUnread = unreadChats.length + unreadGroups.length + pendingInvites.length;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 to-rose-600 p-6 text-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Bell className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold">NOTIFICATIONS</h1>
              <p className="text-red-100">
                {totalUnread > 0 ? `${totalUnread} unattended items` : "All caught up!"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 w-full grid grid-cols-4">
              <TabsTrigger value="all" className="relative">
                All
                {totalUnread > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5">{totalUnread}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="chats" className="relative">
                <MessageSquare className="w-4 h-4 mr-2" />
                Chats
                {unreadChats.length > 0 && (
                  <span className="ml-2 bg-violet-500 text-white text-xs rounded-full px-1.5">{unreadChats.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="groups" className="relative">
                <Users className="w-4 h-4 mr-2" />
                Groups
                {unreadGroups.length > 0 && (
                  <span className="ml-2 bg-blue-500 text-white text-xs rounded-full px-1.5">{unreadGroups.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="invites" className="relative">
                <Video className="w-4 h-4 mr-2" />
                Invites
                {pendingInvites.length > 0 && (
                  <span className="ml-2 bg-emerald-500 text-white text-xs rounded-full px-1.5">{pendingInvites.length}</span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* All Tab */}
            <TabsContent value="all">
              <div className="space-y-4">
                {/* Pending Invites */}
                {pendingInvites.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Meeting Invites
                    </h3>
                    <div className="bg-white rounded-xl shadow-sm border divide-y">
                      {pendingInvites.map(invite => (
                        <div key={invite.id} className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <Video className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{invite.title}</p>
                              <p className="text-sm text-slate-500">
                                From {invite.creator_name} • {invite.scheduled_at ? formatDistanceToNow(new Date(invite.scheduled_at), { addSuffix: true }) : "TBD"}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => respondToInvite(invite.id, "decline")} className="text-red-600 border-red-200">
                              <X className="w-4 h-4" />
                            </Button>
                            <Button size="sm" onClick={() => respondToInvite(invite.id, "confirm")} className="bg-emerald-500 hover:bg-emerald-600">
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Unread Chats */}
                {unreadChats.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Unread Messages
                    </h3>
                    <div className="bg-white rounded-xl shadow-sm border divide-y">
                      {unreadChats.map(chat => (
                        <Link key={chat.id} to={`/comms-centre/chat?user=${chat.from.user_id}`} className="block p-4 hover:bg-app">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarFallback className="bg-violet-100 text-violet-600">
                                  {getInitials(chat.from.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-slate-800">{chat.from.name}</p>
                                <p className="text-sm text-slate-500 truncate max-w-xs">{chat.lastMessage}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="bg-violet-500 text-white text-xs rounded-full px-2 py-0.5">
                                {chat.unreadCount}
                              </span>
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Unread Groups */}
                {unreadGroups.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Group Messages
                    </h3>
                    <div className="bg-white rounded-xl shadow-sm border divide-y">
                      {unreadGroups.map(group => (
                        <Link key={group.id} to={`/comms-centre/groups?group=${group.id}`} className="block p-4 hover:bg-app">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800">{group.name}</p>
                                <p className="text-sm text-slate-500 truncate max-w-xs">{group.lastMessage}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                                {group.unreadCount}
                              </span>
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {totalUnread === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
                    <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <h3 className="font-semibold text-slate-800">All caught up!</h3>
                    <p className="text-slate-500 text-sm">You have no unattended communications</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Chats Tab */}
            <TabsContent value="chats">
              {unreadChats.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm border divide-y">
                  {unreadChats.map(chat => (
                    <Link key={chat.id} to={`/comms-centre/chat?user=${chat.from.user_id}`} className="block p-4 hover:bg-app">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-violet-100 text-violet-600">
                              {getInitials(chat.from.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-800">{chat.from.name}</p>
                            <p className="text-sm text-slate-500 truncate max-w-xs">{chat.lastMessage}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {formatDistanceToNow(new Date(chat.timestamp), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                        <span className="bg-violet-500 text-white text-xs rounded-full px-2 py-0.5">
                          {chat.unreadCount} new
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No unread chat messages</p>
                </div>
              )}
            </TabsContent>

            {/* Groups Tab */}
            <TabsContent value="groups">
              {unreadGroups.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm border divide-y">
                  {unreadGroups.map(group => (
                    <Link key={group.id} to={`/comms-centre/groups?group=${group.id}`} className="block p-4 hover:bg-app">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Users className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{group.name}</p>
                            <p className="text-sm text-slate-500 truncate max-w-xs">{group.lastMessage}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {formatDistanceToNow(new Date(group.timestamp), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                        <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">
                          {group.unreadCount} new
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No unread group messages</p>
                </div>
              )}
            </TabsContent>

            {/* Invites Tab */}
            <TabsContent value="invites">
              {pendingInvites.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm border divide-y">
                  {pendingInvites.map(invite => (
                    <div key={invite.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <Video className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{invite.title}</p>
                            <p className="text-sm text-slate-500">Invited by {invite.creator_name}</p>
                            {invite.description && (
                              <p className="text-sm text-slate-600 mt-1">{invite.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                              <Clock className="w-3 h-3" />
                              {invite.scheduled_at ? new Date(invite.scheduled_at).toLocaleString() : "Time TBD"}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => respondToInvite(invite.id, "decline")}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => respondToInvite(invite.id, "confirm")}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-xl shadow-sm border">
                  <Video className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No pending meeting invitations</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
